import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { BackgroundJob, JobStatus, JobType } from '../../types';

export interface JobDetailsDialogProps {
  job: BackgroundJob | null;
  visible: boolean;
  onHide: () => void;
}

const jobTypeLabels: Record<JobType, string> = {
  identify_customers: 'Identify Customers',
  parse_bank_statement: 'Parse Bank Statement',
  parse_deposits: 'Parse Deposits',
};

const statusSeverityMap: Record<JobStatus, 'success' | 'info' | 'warning' | 'danger'> = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'danger',
};

export const formatJobType = (type: JobType): string => {
  return jobTypeLabels[type] || type;
};

const formatDuration = (job: BackgroundJob): string => {
  if (!job.started_at) return '-';
  const start = new Date(job.started_at);
  const end = job.completed_at ? new Date(job.completed_at) : new Date();
  const durationMs = end.getTime() - start.getTime();
  const seconds = Math.floor(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const getProgressPercent = (job: BackgroundJob): number => {
  if (job.progress_total <= 0) return 0;
  return Math.round((job.progress_current / job.progress_total) * 100);
};

export const JobDetailsDialog: React.FC<JobDetailsDialogProps> = ({ job, visible, onHide }) => {
  if (!job) return null;

  const percent = getProgressPercent(job);
  const showProgress = job.status === 'running' || job.status === 'pending';

  return (
    <Dialog
      header="Job Details"
      visible={visible}
      onHide={onHide}
      className="job-details-dialog"
    >
      <table className="info-table">
        <tbody>
          <tr>
            <th>Type</th>
            <td>{formatJobType(job.job_type)}</td>
          </tr>
          <tr>
            <th>Status</th>
            <td>
              <Tag value={job.status} severity={statusSeverityMap[job.status]} />
            </td>
          </tr>
          {showProgress && (
            <tr>
              <th>Progress</th>
              <td>
                <ProgressBar value={percent} showValue />
                {job.progress_message && (
                  <small className="status-text-muted progress-message">
                    {job.progress_message}
                  </small>
                )}
              </td>
            </tr>
          )}
          <tr>
            <th>Created</th>
            <td>{new Date(job.created_at).toLocaleString()}</td>
          </tr>
          {job.started_at && (
            <tr>
              <th>Started</th>
              <td>{new Date(job.started_at).toLocaleString()}</td>
            </tr>
          )}
          {job.completed_at && (
            <tr>
              <th>Completed</th>
              <td>{new Date(job.completed_at).toLocaleString()}</td>
            </tr>
          )}
          {job.started_at && (
            <tr>
              <th>Duration</th>
              <td>{formatDuration(job)}</td>
            </tr>
          )}
          {job.error && (
            <tr className="error-row">
              <th>Error</th>
              <td>
                <pre>{job.error}</pre>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Dialog>
  );
};

export default JobDetailsDialog;
